import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { AzureDataLakeAnalyticsLinkedService } from 'app/model/linkedservice/azure-data-lake-analytics.linkedservice';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from './azure-key-vault.util';
import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { GlobalVariables } from 'app/services/globals';
import { ReferenceAdapter } from '../reference.adapter';
import { AuthenticationReferenceType, CredentialTypeOptions, Credential } from 'shell-models-lib';

enum SupportedAuthTypes {
    MSI = 'MSI'    
}

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureDataLakeAnalyticsLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                accountName: '*',
                servicePrincipalId: '*',
                servicePrincipalKey: 'secret',
                tenant: '*',
                subscriptionId: '*',
                resourceGroupName: '*',
                encryptedCredential: '*',
                authentication: '*'
            }
        }
    }
})
export class AzureDataLakeAnalyticsLinkedServiceAdapter extends LinkedServiceAdapter<AzureDataLakeAnalyticsLinkedService> {

    /**
     * Update AuthenticationType based on fetched credential type
     * @param model linked service model
     */
    public async postToModel(model: AzureDataLakeAnalyticsLinkedService): Promise<void> {
        let finalAuthType: string = model.authenticationType;
        if (model.credential) {
            if (GlobalVariables.enableUamiAuthForScopeActivity) {
                const credType = model.credential.getType();
                if (credType === CredentialTypeOptions.ServicePrincipal) {
                    finalAuthType = AuthenticationType.ServicePrincipal;
                } else if (credType === CredentialTypeOptions.ManagedIdentity) {
                    finalAuthType = AuthenticationType.ManagedServiceIdentity;
                } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                    finalAuthType = AuthenticationType.UserAssignedManagedIdentity;
                } else {
                    finalAuthType = model.authenticationType;
                }
            } else {
                finalAuthType = AuthenticationType.ManagedServiceIdentity;
            }
        }
        model.authenticationType = finalAuthType;
    }
    
    protected modelConstructor(): new () => AzureDataLakeAnalyticsLinkedService {
        return AzureDataLakeAnalyticsLinkedService;
    }

    protected model(): IModel {
        return AzureDataLakeAnalyticsLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureDataLakeAnalyticsLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureDataLakeAnalyticsLinkedService;
        properties.type = Metamodel.modelFor(AzureDataLakeAnalyticsLinkedService).name;
        if (GlobalVariables.enableMsiAuthForScopeActivity
            && (linkedService.authenticationType === AuthenticationType.ManagedServiceIdentity || linkedService.authenticationType === AuthenticationType.UserAssignedManagedIdentity)) {
            properties.typeProperties = {
                accountName: linkedService.accountName,
                authentication: SupportedAuthTypes.MSI,
                subscriptionId: linkedService.subscriptionId,
                resourceGroupName: linkedService.resourceGroupName,
                credential: 
                    GlobalVariables.enableUamiAuthForScopeActivity && linkedService.credential
                    && ((linkedService.authenticationType === AuthenticationType.ManagedServiceIdentity && linkedService.authenticationReferenceType === AuthenticationReferenceType.Credential)
                        || linkedService.authenticationType === AuthenticationType.UserAssignedManagedIdentity) 
                        ? ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference 
                        : undefined
            };
        } else {
            properties.typeProperties = {
                accountName: linkedService.accountName,
                servicePrincipalId: linkedService.servicePrincipalId,
                servicePrincipalKey: passwordOrAKVtoSecretBase(linkedService.servicePrincipalKey, this.context),
                tenant: linkedService.tenant,
                subscriptionId: linkedService.subscriptionId,
                resourceGroupName: linkedService.resourceGroupName
            };
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureDataLakeAnalyticsLinkedService): Promise<AzureDataLakeAnalyticsLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureDataLakeAnalyticsLinkedService;
        linkedService.accountName = properties.typeProperties.accountName;
        if (GlobalVariables.enableMsiAuthForScopeActivity && properties.typeProperties.authentication === SupportedAuthTypes.MSI)  {
            // set correct auth method in postToModel call if credential reference is present.
            linkedService.authenticationType = AuthenticationType.ManagedServiceIdentity;
            linkedService.servicePrincipalId = undefined;
            linkedService.servicePrincipalKey = undefined;
            linkedService.tenant = undefined;
            if (GlobalVariables.enableUamiAuthForScopeActivity && properties.typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), properties.typeProperties.credential, this.context);
                linkedService.authenticationReferenceType = AuthenticationReferenceType.Credential;
            }
        } else {
            linkedService.authenticationType = GlobalVariables.enableMsiAuthForScopeActivity ? AuthenticationType.ServicePrincipal : undefined;
            linkedService.servicePrincipalId = properties.typeProperties.servicePrincipalId;
            linkedService.servicePrincipalKey = secretBaseToPasswordOrAKV(properties.typeProperties.servicePrincipalKey, true, this.context);
            linkedService.tenant = properties.typeProperties.tenant;
        }
        linkedService.subscriptionId = properties.typeProperties.subscriptionId;
        linkedService.resourceGroupName = properties.typeProperties.resourceGroupName;
        
        return linkedService;
    }
}
